import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Button,
} from "@mui/material";
import SurroundSoundRoundedIcon from "@mui/icons-material/SurroundSoundRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import CompareArrowsRoundedIcon from "@mui/icons-material/CompareArrowsRounded";
import LibraryMusicRoundedIcon from "@mui/icons-material/LibraryMusicRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import OpenInFullRoundedIcon from "@mui/icons-material/OpenInFullRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import Seo from "../../components/Seo";
import { Link as RouterLink } from "react-router-dom";
import { useStyle } from "../../context/styleContext/styleContext";

export default function StereoImagingLearning() {
  const { style } = useStyle();
  const [height, setHeight] = useState(window.innerHeight);
  const topContainer = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    topContainer.current?.scrollIntoView({ block: "start" });
  }, []);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const accent = "rgb(88, 166, 124)";
  const pageUrl = "https://parishaudio.com/learning/stereo-imaging";
  const pageTitle = "Stereo Imaging in Music Production: What It Is and How It Works";
  const pageDescription =
    "Learn what stereo imaging is, how width and placement affect a mix, what mono compatibility means, and how stereo imaging is used in music production and mastering.";

  const schema = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        "@id": `${pageUrl}#article`,
        headline: pageTitle,
        description: pageDescription,
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": `${pageUrl}#webpage`,
        },
        url: pageUrl,
        inLanguage: "en-US",
        articleSection: "Audio Education",
        keywords: [
          "stereo imaging",
          "what is stereo imaging",
          "stereo width",
          "mono compatibility",
          "mid side processing",
          "stereo field",
          "mix width",
        ],
        author: {
          "@type": "Organization",
          name: "Parish Audio",
          url: "https://parishaudio.com",
        },
        publisher: {
          "@type": "Organization",
          name: "Parish Audio",
          url: "https://parishaudio.com",
          logo: {
            "@type": "ImageObject",
            url: "https://parishaudio.com/assets/logo.png",
          },
        },
        image: {
          "@type": "ImageObject",
          url: "https://parishaudio.com/RecordingStudioStockImage1.jpg",
        },
        about: [
          { "@type": "Thing", name: "Stereo imaging" },
          { "@type": "Thing", name: "Stereo width" },
          { "@type": "Thing", name: "Mid/side processing" },
          { "@type": "Thing", name: "Mono compatibility" },
          { "@type": "Thing", name: "Mixing" },
          { "@type": "Thing", name: "Mastering" },
        ],
        isPartOf: {
          "@type": "CollectionPage",
          name: "Parish Audio Learning Center",
          url: "https://parishaudio.com/learning",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "@id": `${pageUrl}#breadcrumbs`,
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://parishaudio.com",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Learning Center",
            item: "https://parishaudio.com/learning",
          },
          {
            "@type": "ListItem",
            position: 3,
            name: "Stereo Imaging",
            item: pageUrl,
          },
        ],
      },
    ],
    []
  );

  const quickFacts = [
    {
      title: "What it does",
      text: "Stereo imaging shapes how wide, narrow, centered, or spacious a sound or full mix feels across the left and right speakers.",
      icon: <SurroundSoundRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Why it matters",
      text: "Good stereo imaging helps a mix feel bigger, clearer, and more immersive while giving each element a more intentional place.",
      icon: <OpenInFullRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Where it is used",
      text: "Mixing, mastering, stereo enhancement, panning decisions, ambience control, bus processing, and sound design.",
      icon: <GraphicEqRoundedIcon sx={{ color: accent }} />,
    },
  ];

  const imagingTypes = [
    {
      name: "Panning-Based Imaging",
      description:
        "Creates width and separation by placing sounds at different points between the left and right channels.",
    },
    {
      name: "Mid/Side Imaging",
      description:
        "Separates the center information from the side information, allowing width changes without affecting both the same way.",
    },
    {
      name: "Delay-Based Widening",
      description:
        "Uses tiny timing differences between left and right to create a wider stereo impression, sometimes inspired by the Haas effect.",
    },
    {
      name: "Modulation-Based Width",
      description:
        "Uses chorus, flanger, phaser, or micro-pitch style effects to add motion and make a source feel wider.",
    },
    {
      name: "Ambience-Based Imaging",
      description:
        "Uses reverb or room information to push a sound outward and create a larger sense of space around it.",
    },
    {
      name: "Mastering Width Control",
      description:
        "Applies careful stereo shaping to the full mix, often with subtle adjustments to preserve focus and mono compatibility.",
    },
  ];

  const imagingConcepts = [
    {
      name: "Center vs Sides",
      description:
        "The center contains information shared equally by the left and right channels, while the sides contain the differences that create width.",
    },
    {
      name: "Stereo Width",
      description:
        "Width describes how far a sound appears to spread across the stereo field, from narrow and centered to wide and spacious.",
    },
    {
      name: "Depth and Space",
      description:
        "Stereo imaging is not only about left and right. It also influences how open, deep, and three-dimensional a mix feels.",
    },
    {
      name: "Mono Compatibility",
      description:
        "A very wide sound can become weak or strange when collapsed to mono if the left and right channels differ too much.",
    },
  ];

  const controls = [
    {
      name: "Width",
      description:
        "Adjusts how wide the stereo image feels. Higher settings often increase side information relative to the center.",
    },
    {
      name: "Pan",
      description:
        "Moves an element left or right in the stereo field, which is one of the most basic and powerful imaging tools.",
    },
    {
      name: "Mid / Side Balance",
      description:
        "Controls the relationship between center content and side content, allowing more precise image shaping.",
    },
    {
      name: "Delay / Offset",
      description:
        "Some stereo wideners use tiny timing offsets between left and right to create more apparent width.",
    },
    {
      name: "Frequency-Dependent Width",
      description:
        "Lets you widen only certain parts of the spectrum, which is useful because low frequencies often need to stay more centered.",
    },
    {
      name: "Stereo Link / Correlation Monitoring",
      description:
        "Helps track whether your width settings remain stable and mono-safe rather than becoming phasey or hollow.",
    },
  ];

  const commonUses = [
    "Placing instruments so they do not all fight for the center",
    "Making pads, backing vocals, and effects feel larger and more immersive",
    "Keeping kick, snare, bass, and lead vocal focused in the middle",
    "Adding width to guitars, synths, and ambience without overwhelming the mix",
    "Refining the sense of space in a mastering chain",
    "Creating contrast between narrow verses and wider choruses",
  ];

  const bestPractices = [
    "Treat width as a musical decision, not just a way to make everything sound bigger.",
    "Keep important low-end elements mostly centered unless you have a clear reason not to.",
    "Check your mix in mono regularly, especially when using wideners or heavy stereo effects.",
    "Use contrast. A mix often feels wider when some things stay narrow and focused.",
    "Be careful with extreme widening on the full mix, because it can weaken punch and create phase issues.",
  ];

  const relatedLinks = [
    {
      title: "Equalization",
      to: "/learning/equalization",
      description:
        "Learn how tonal balance affects clarity and separation, which strongly influences perceived width and space.",
    },
    {
      title: "Reverb",
      to: "/learning/reverb",
      description:
        "See how reverb adds depth and space, which often works hand in hand with stereo imaging.",
    },
    {
      title: "Chorus",
      to: "/learning/chorus",
      description:
        "Explore how chorus can widen and thicken sounds using modulation and subtle stereo movement.",
    },
    {
      title: "Phaser",
      to: "/learning/phaser",
      description:
        "Understand how phasers add movement and motion, which can interact with stereo placement and width.",
    },
  ];

  const outerPanelSx = {
    width: "100%",
    minWidth: 0,
    borderRadius: "20px",
    overflow: "hidden",
    backgroundColor: style.color1,
    border: `1px solid ${style.color2}`,
    boxSizing: "border-box",
  };

  const subtlePanelSx = {
    borderRadius: "14px",
    backgroundColor: style.darkerPanel,
    border: "1px solid rgba(255,255,255,0.06)",
    boxShadow: "none",
  };

  return (
    <>
      <Seo
        title="Stereo Imaging | Parish Audio Learning"
        description={pageDescription}
        canonical={pageUrl}
        schema={schema}
      />

      <Box
        ref={topContainer}
        sx={{
          width: "100%",
          maxWidth: "100vw",
          overflowX: "hidden",
          backgroundColor: "black",
          px: { xs: 1.25, sm: 1.5, md: 2.5 },
          py: { xs: 1.5, md: 2.5 },
          boxSizing: "border-box",
          minHeight: height - 130,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 1120, mx: "auto", minWidth: 0 }}>
          <Stack spacing={{ xs: 2, md: 2.5 }}>
            <Paper
              elevation={0}
              sx={{
                ...outerPanelSx,
                minHeight: { xs: 260, md: 310 },
                display: "flex",
                alignItems: "center",
                backgroundImage:
                  'linear-gradient(90deg, rgba(8,12,18,0.84) 0%, rgba(8,12,18,0.74) 38%, rgba(8,12,18,0.42) 68%, rgba(8,12,18,0.22) 100%), url("/RecordingStudioStockImage1.jpg")',
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, width: "100%" }}>
                <Box sx={{ maxWidth: 760 }}>
                  <Stack spacing={1.25} alignItems="flex-start">
                    <Chip
                      label="Learning Center"
                      sx={{
                        borderRadius: "5px",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor: "rgba(255,255,255,0.08)",
                        border: "1px solid rgba(255,255,255,0.10)",
                      }}
                    />

                    <Typography
                      variant="h1"
                      sx={{
                        fontSize: { xs: "2.2rem", md: "3.35rem" },
                        fontWeight: 800,
                        lineHeight: 1.02,
                        color: style.color4,
                        letterSpacing: "-0.04em",
                      }}
                    >
                      Stereo Imaging in Music Production
                    </Typography>

                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.86)",
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        lineHeight: 1.75,
                        maxWidth: 700,
                      }}
                    >
                      Stereo imaging is the art and science of shaping how sound is distributed
                      across the left and right speakers. It affects whether a sound feels narrow,
                      centered, wide, spacious, or immersive, and it plays a huge role in how open
                      and professional a mix feels.
                    </Typography>

                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.86)",
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        lineHeight: 1.75,
                        maxWidth: 700,
                      }}
                    >
                      Good stereo imaging helps create separation, depth, and contrast. It can make
                      a chorus feel huge, a vocal feel focused, a guitar feel expansive, or a full
                      mix feel balanced and intentional. Done poorly, it can make a mix feel blurry,
                      phasey, or weak when played back in mono.
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                gap: 2,
                alignItems: "stretch",
              }}
            >
              {quickFacts.map((item) => (
                <Paper key={item.title} elevation={0} sx={{ ...outerPanelSx }}>
                  <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      {item.icon}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: style.color4,
                            mb: 0.5,
                            lineHeight: 1.2,
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: style.color3,
                            lineHeight: 1.55,
                          }}
                        >
                          {item.text}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Paper>
              ))}
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Typography
                    sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                  >
                    What is stereo imaging?
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                    Stereo imaging refers to how audio is positioned and perceived between the left
                    and right channels of a stereo playback system. It influences where sounds seem
                    to sit, how wide they feel, and how much dimension a mix appears to have.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                    Some of stereo imaging comes from simple panning. Some comes from natural
                    differences between microphones, room reflections, reverb, timing offsets,
                    modulation, or dedicated stereo processing tools. Together, these factors shape
                    the sense of size, spread, and realism in a mix.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                    Stereo imaging is not just about making things wide. It is about making the
                    stereo field feel intentional. A focused center can make a lead vocal feel
                    strong. Carefully placed sides can make a mix feel large without losing clarity.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={2}>
                  <Typography
                    sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                  >
                    How stereo imaging works
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {imagingConcepts.map((item) => (
                      <Paper
                        key={item.name}
                        elevation={0}
                        sx={{ ...subtlePanelSx, p: 1.5 }}
                      >
                        <Typography sx={{ color: accent, fontWeight: 700, mb: 0.4 }}>
                          {item.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: style.color3, lineHeight: 1.65 }}
                        >
                          {item.description}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>

                  <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                    When the left and right channels are identical, the sound feels centered.
                    As the differences between them increase, the image can feel wider. Those
                    differences might come from level, timing, frequency balance, ambience, or
                    phase relationships. Stereo imaging tools shape those differences to control
                    how the listener perceives space.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={2}>
                  <Typography
                    sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                  >
                    Common stereo imaging approaches
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {imagingTypes.map((item) => (
                      <Paper
                        key={item.name}
                        elevation={0}
                        sx={{ ...subtlePanelSx, p: 1.5 }}
                      >
                        <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.4 }}>
                          {item.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: style.color3, lineHeight: 1.65 }}
                        >
                          {item.description}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CompareArrowsRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Stereo imaging vs panning
                    </Typography>
                  </Stack>

                  <Paper elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                    <Stack spacing={1.2}>
                      <Typography sx={{ color: style.color4, fontWeight: 700 }}>
                        Related, but not identical
                      </Typography>

                      <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                        Panning is one of the main building blocks of stereo imaging, but stereo
                        imaging is a broader concept. Panning decides where a sound sits from left
                        to right. Stereo imaging also includes width, ambience, side content, and
                        the overall spatial relationships between elements.
                      </Typography>

                      <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                        In other words, panning places sounds. Stereo imaging shapes the whole
                        perceived space around them. A sound can be centered but still feel wide,
                        or panned but still feel narrow, depending on how it is processed.
                      </Typography>
                    </Stack>
                  </Paper>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TuneRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Common stereo imaging controls
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {controls.map((item) => (
                      <Paper
                        key={item.name}
                        elevation={0}
                        sx={{ ...subtlePanelSx, p: 1.5 }}
                      >
                        <Stack direction="row" spacing={1.1} alignItems="flex-start">
                          <TuneRoundedIcon sx={{ color: accent, mt: "2px", flexShrink: 0 }} />
                          <Box>
                            <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.4 }}>
                              {item.name}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: style.color3, lineHeight: 1.65 }}
                            >
                              {item.description}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2.25,
                alignItems: "stretch",
              }}
            >
              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <LibraryMusicRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        How stereo imaging is used in mixing and mastering
                      </Typography>
                    </Stack>

                    <Stack spacing={1.1}>
                      {commonUses.map((item) => (
                        <Stack key={item} direction="row" spacing={1.1} alignItems="flex-start">
                          <LibraryMusicRoundedIcon
                            sx={{ color: accent, mt: "2px", fontSize: 20, flexShrink: 0 }}
                          />
                          <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                            {item}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                      In a mix, stereo imaging helps prevent everything from stacking on top of the
                      lead vocal and snare in the center. In mastering, it is often used more subtly
                      to refine width, preserve focus, and create a balanced sense of openness without
                      damaging mono playback or making the mix feel unstable.
                    </Typography>
                  </Stack>
                </Box>
              </Paper>

              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <WarningAmberRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Common stereo imaging mistakes
                      </Typography>
                    </Stack>

                    <Stack spacing={1.35}>
                      {[
                        "Making everything wide, which removes contrast and can actually make the mix feel less impressive.",
                        "Widening low frequencies too much, which can weaken the center and reduce playback stability.",
                        "Ignoring mono compatibility and ending up with phase problems or disappearing elements.",
                        "Using wideners to fix arrangement problems that would be better solved with panning, EQ, or level balance.",
                      ].map((item) => (
                        <Stack key={item} direction="row" spacing={1.1} alignItems="flex-start">
                          <WarningAmberRoundedIcon sx={{ color: accent, mt: "2px", flexShrink: 0 }} />
                          <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                            {item}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              </Paper>
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TipsAndUpdatesRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Best practices for stereo imaging
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {bestPractices.map((item, index) => (
                      <Paper
                        key={index}
                        elevation={0}
                        sx={{ ...subtlePanelSx, p: 1.5 }}
                      >
                        <Stack direction="row" spacing={1.1} alignItems="flex-start">
                          <TipsAndUpdatesRoundedIcon sx={{ color: accent, mt: "2px", flexShrink: 0 }} />
                          <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                            {item}
                          </Typography>
                        </Stack>
                      </Paper>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Typography
                    sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                  >
                    Related learning topics
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "start",
                    }}
                  >
                    {relatedLinks.map((item) => (
                      <Box
                        key={item.title}
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "flex-start",
                        }}
                      >
                        <Paper
                          elevation={0}
                          sx={{
                            ...subtlePanelSx,
                            p: 1.5,
                            width: "100%",
                            minHeight: "75%",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                          }}
                        >
                          <Box>
                            <Typography
                              sx={{ color: style.color4, fontWeight: 700, mb: 0.75 }}
                            >
                              {item.title}
                            </Typography>
                            <Typography
                              sx={{ color: style.color3, lineHeight: 1.75, mb: 2 }}
                            >
                              {item.description}
                            </Typography>
                          </Box>

                          <Box>
                            <Button
                              component={RouterLink}
                              to={item.to}
                              sx={{
                                borderRadius: "5px",
                                textTransform: "none",
                                fontWeight: 700,
                                color: style.color4,
                                backgroundColor: "rgba(255,255,255,0.05)",
                                border: `1px solid ${accent}`,
                                "&:hover": {
                                  borderColor: style.color4,
                                  backgroundColor: "rgba(255,255,255,0.10)",
                                },
                              }}
                            >
                              Read More
                            </Button>
                          </Box>
                        </Paper>
                      </Box>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <AutoAwesomeRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Final thoughts
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.9 }}>
                    Stereo imaging is one of the biggest contributors to how spacious and
                    professional a mix feels. It helps create size, contrast, and separation,
                    but it works best when used with intention rather than pushed everywhere.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.9 }}>
                    A great stereo image is not only wide. It is balanced, focused, and musical.
                    When center and sides work together well, the mix feels open without losing
                    strength, clarity, or mono compatibility.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
                <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.7 }}>
                  * We are not affiliated with any brands or artists mentioned on this site. *
                </Typography>
              </Box>
            </Paper>
          </Stack>
        </Box>
      </Box>
    </>
  );
}